import React, { useEffect, useState, useRef } from 'react';
import * as THREE from 'three';
import axios from 'axios';
import APP_BASE_URL, { IMG_BASE_URL } from '../../components/Api/globalApi';
import './VirtualMall.css';
import { TbMapPin, TbSearch, TbX, TbHeart, TbMessageCircle, TbDownload, TbChevronLeft, TbChevronRight } from "react-icons/tb";
import { useNavigate } from 'react-router-dom';
import { io } from 'socket.io-client';
import Swal from 'sweetalert2';
const imgBaseurl = IMG_BASE_URL;

// Memoized Ad Card to prevent shivering during 3D scene renders
const AdCard = React.memo(({ ad, index, activeAdIndex, isHoveredRef, adLikes, adComments, handleNextAd, handlePrevAd, handleLike, handleComment, handleDownload, imgBaseurl }) => {
  return (
    <div
      key={ad.sa_id}
      className={`ad-card ${index === activeAdIndex ? 'active' : ''}`}
      onMouseEnter={() => { isHoveredRef.current = true; }}
      onMouseLeave={() => { isHoveredRef.current = false; }}
      onClick={() => { isHoveredRef.current = !isHoveredRef.current; }}
    >
      <div className="ad-shine"></div>
      <div className="ad-image-wrap" style={{ '--ad-bg-img': `url(${imgBaseurl}/${ad.sa_image})` }}>
        <img className="ad-image" src={`${imgBaseurl}/${ad.sa_image}`} alt={ad.sa_title} />

        <button className="ad-nav-btn prev" onClick={(e) => { e.stopPropagation(); handlePrevAd(); }} aria-label="Previous advertisement">
          <TbChevronLeft />
        </button>
        <button className="ad-nav-btn next" onClick={(e) => { e.stopPropagation(); handleNextAd(); }} aria-label="Next advertisement">
          <TbChevronRight />
        </button>

        <div className="ad-point-floating-badge">
          <span className="plus">+</span>
          <span className="pts-val">{ad.sa_points_per_like || 1}</span>
          <span className="pts-label">PTS</span>
        </div>
      </div>
      <div className="ad-content">
        <h3>{ad.sa_title}</h3>
        <p>{ad.sa_description}</p>

        <div className="ad-actions">
          <button className="ad-btn like" onClick={(e) => { e.stopPropagation(); handleLike(ad); }}>
            <TbHeart className="ad-btn-icon" />
            <span className="count">{adLikes[String(ad.sa_id)] || 0}</span>
          </button>
          <button className="ad-btn comment" onClick={(e) => { e.stopPropagation(); handleComment(ad); }}>
            <TbMessageCircle className="ad-btn-icon" />
          </button>
          <button className="ad-btn download" onClick={(e) => { e.stopPropagation(); handleDownload(ad); }}>
            <TbDownload className="ad-btn-icon" />
          </button>
        </div>

        <div className="ad-comments-section">
          <div className="ad-comments-list">
            {(adComments[String(ad.sa_id)] || []).length > 0 ? (
              (adComments[String(ad.sa_id)] || []).map((c, i) => (
                <div key={i} className="ad-comment-item">
                  <span className="comment-user">{c.user_name || 'Guest'}:</span>
                  <span className="comment-text">{c.vsc_comment}</span>
                </div>
              ))
            ) : (
              <div className="no-comments">No comments yet</div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
});

const VirtualMall = () => {
  const navigate = useNavigate();
  const containerRef = useRef(null);
  const [activeAdIndex, setActiveAdIndex] = useState(0);
  const isHoveredRef = useRef(false);
  const [ads, setAds] = useState([]);
  const [shops, setShops] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentShop, setCurrentShop] = useState(null);
  const [currentIdx, setCurrentIdx] = useState(0);
  const [cardVisible, setCardVisible] = useState(false);
  const [activeSide, setActiveSide] = useState('right');
  const [searchTerm, setSearchTerm] = useState('');
  const [isSearchFocused, setIsSearchFocused] = useState(false);

  const [adLikes, setAdLikes] = useState({}); // { adId: likeCount }
  const [adComments, setAdComments] = useState({}); // { adId: [comments] }
  const [incentiveWallet, setIncentiveWallet] = useState(0);
  const [activityWallet, setActivityWallet] = useState(0);
  const [likedAdPoints, setLikedAdPoints] = useState(0);
  const [purchasePoints, setPurchasePoints] = useState(0);
  const socketRef = useRef(null);

  const userId = localStorage.getItem("virtual_user_id");
  const isAuthenticated = !!userId;

  const handleShopClick = () => {
    if (currentShop?.id) {
      navigate(`/shop-products?shop_id=${currentShop.id}`);
    }
  };

  const filteredShops = searchTerm.trim()
    ? shops.filter((shop) => {
      const query = searchTerm.trim().toLowerCase();
      return (
        shop.virtual_mall_shop_name?.toLowerCase().includes(query) ||
        shop.tagline?.toLowerCase().includes(query) ||
        shop.category?.toLowerCase().includes(query) ||
        shop.district?.toLowerCase().includes(query) ||
        shop.code?.toLowerCase().includes(query)
      );
    })
    : [];

  const jumpToShop = (shop, fromSearch = false) => {
    const mallContainer = containerRef.current;
    const shopIndex = shops.findIndex((item) => item.id === shop.id);

    if (!mallContainer || shopIndex === -1 || shops.length === 0) {
      return;
    }

    const maxScroll = mallContainer.offsetHeight - window.innerHeight;
    const targetProgress = shops.length > 1 ? shopIndex / (shops.length - 1) : 0;
    const targetTop = mallContainer.offsetTop + maxScroll * targetProgress;

    setCurrentShop(shop);
    setCurrentIdx(shopIndex);
    setCardVisible(true);
    setActiveSide(shopIndex % 2 === 0 ? 'right' : 'left');
    
    if (fromSearch) {
      setSearchTerm(shop.virtual_mall_shop_name || '');
    }

    window.scrollTo({
      top: targetTop,
      behavior: 'smooth'
    });
  };

  useEffect(() => {
    const fetchShops = async () => {
      try {
        const response = await axios.get(`${APP_BASE_URL}/admin/get-shops`);
        if (response.data.status) {
          const mappedShops = response.data.data.map((s, i) => {
            const accents = [0xFF9900, 0x6633FF, 0x00C49F, 0xFF007F, 0x0088FF, 0x00D4FF, 0xFF4D4D, 0x9D50BB];
            return {
              id: s.s_id,
              virtual_mall_shop_name: s.s_name || "New Shop",
              tagline: s.s_address || "Coming Soon",
              category: s.s_category || "",
              district: s.s_district || "",
              code: s.s_code || "",
              open: s.s_status === 1,
              accent: accents[i % accents.length],
              accent2: accents[(i + 1) % accents.length],
              facade: 0xFFFFFF,
              sign: accents[i % accents.length],
              image: s.s_image ? `${imgBaseurl}/${s.s_image}` : null
            };
          });
          setShops(mappedShops);
          if (mappedShops.length > 0) {
            setCurrentShop(mappedShops[0]);
            setCardVisible(true);
          }
        }
      } catch (error) {
        console.error("Error fetching shops:", error);
      } finally {
        setLoading(false);
      }
    };
    const fetchAds = async () => {
      try {
        const response = await axios.get(`${APP_BASE_URL}/shop-ads/list`);
        if (response.data.status) {
          setAds(response.data.data);
        }
      } catch (error) {
        console.error("Error fetching ads:", error);
      }
    };

    const fetchIncentiveWallet = async () => {
      try {
        const token = localStorage.getItem("virtualmall_token");
        if (!token) return;

        const res = await axios.post(
          `${APP_BASE_URL}/virtualuser/incentive/wallet/history`,
          {},
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
        if (res.data.result) {
          setIncentiveWallet(parseFloat(res.data.incentiveWallet) || 0);
          setActivityWallet(parseFloat(res.data.activityWallet) || 0);
          setLikedAdPoints(parseFloat(res.data.likedAdPoints) || 0);
          setPurchasePoints(parseFloat(res.data.purchasePoints) || 0);
        }
      } catch (err) {
        console.error("Error fetching incentive wallet:", err);
      }
    };

    fetchShops();
    fetchAds();
    fetchIncentiveWallet();

    // Socket Initialization
    socketRef.current = io(APP_BASE_URL.replace('/mlm', '')); // Backend server root for socket

    socketRef.current.on('ad_interaction', (data) => {
      if (data.type === 'like') {
        const adId = String(data.a_id);
        const newCount = Number(data.like_count);
        setAdLikes(prev => ({ ...prev, [adId]: newCount }));
      } else if (data.type === 'comment') {
        const adId = String(data.a_id);
        const newComment = {
          vsc_u_id: data.user_id,
          vsc_comment: data.comment,
          user_name: data.user_name || 'User',
          vsc_date: new Date().toISOString().split('T')[0]
        };
        setAdComments(prev => ({
          ...prev,
          [adId]: [newComment, ...(prev[adId] || [])].slice(0, 10)
        }));
      }
    });

    return () => {
      if (socketRef.current) socketRef.current.disconnect();
    };
  }, []);

  const handleNextAd = () => {
    if (ads.length === 0) return;
    setActiveAdIndex((prev) => (prev + 1) % ads.length);
  };

  const handlePrevAd = () => {
    if (ads.length === 0) return;
    setActiveAdIndex((prev) => (prev - 1 + ads.length) % ads.length);
  };

  useEffect(() => {
    let timer;
    if (ads.length > 0) {
      timer = setInterval(() => {
        // Use ref value to check if we should skip the update
        if (!isHoveredRef.current) {
          setActiveAdIndex((prev) => (prev + 1) % ads.length);
        }
      }, 5000);
    }
    return () => clearInterval(timer);
  }, [ads.length]);

  useEffect(() => {
    if (ads.length > 0) {
      const initialLikes = {};
      const initialComments = {};
      ads.forEach(ad => {
        initialLikes[String(ad.sa_id)] = Number(ad.sa_likes) || 0;
        initialComments[String(ad.sa_id)] = ad.comments || [];
      });
      setAdLikes(initialLikes);
      setAdComments(initialComments);
    }
  }, [ads]);

  const checkAuth = () => {
    if (!isAuthenticated) {
      navigate(`/virtual_mall_login?redirect=${encodeURIComponent(window.location.pathname)}`);
      return false;
    }
    return true;
  };

  const handleLike = async (ad) => {
    if (!checkAuth()) return;
    try {
      const response = await axios.post(`${APP_BASE_URL}/vitual_mall/ads/like`, {
        a_id: ad.sa_id,
        user_id: userId
      });
      if (response.data.like_count !== undefined) {
        const adId = String(ad.sa_id);
        const newCount = Number(response.data.like_count);
        setAdLikes(prev => ({ ...prev, [adId]: newCount }));

        // Dynamically update Activity Wallet balance
        if (response.data.result) {
          const pointsEarned = Number(ad.sa_points_per_like) || 1;
          setActivityWallet(prev => prev + pointsEarned);
        }
      }

      if (response.data.message) {
        Swal.fire({
          icon: response.data.result ? 'success' : 'info',
          title: response.data.result ? 'Success' : 'Notice',
          text: response.data.message,
          background: '#1A1E2E',
          color: '#fff',
          timer: 2000,
          showConfirmButton: false,
          toast: true,
          position: 'top-end'
        });
      }
    } catch (error) {
      console.error("Like error:", error);
    }
  };

  const handleComment = async (ad) => {
    if (!checkAuth()) return;

    const { value: text } = await Swal.fire({
      title: 'Add a Comment',
      input: 'textarea',
      inputPlaceholder: 'Write your thoughts about this ad...',
      background: '#1A1E2E',
      color: '#fff',
      confirmButtonColor: '#6366F1',
      showCancelButton: true,
      cancelButtonColor: '#d33'
    });

    if (text) {
      try {
        const response = await axios.post(`${APP_BASE_URL}/vitual_mall/ads/comment`, {
          a_id: ad.sa_id,
          comment: text,
          user_id: userId,
          phone_number: "0000000000" // Placeholder as backend needs it but user might not have it in local storage yet
        });
        if (response.data.result) {
          Swal.fire({
            icon: 'success',
            title: 'Success',
            text: 'Comment posted!',
            timer: 1500,
            showConfirmButton: false,
            background: '#1A1E2E',
            color: '#fff'
          });
        }
      } catch (error) {
        console.error("Comment error:", error);
      }
    }
  };

  const handleDownload = async (ad) => {
    if (!checkAuth()) return;

    const imageUrl = `${imgBaseurl}/${ad.sa_image}`;

    try {
      const response = await fetch(imageUrl, { mode: 'cors' });

      if (!response.ok) {
        throw new Error(`Failed to fetch image: ${response.status} ${response.statusText}`);
      }

      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);

      const link = document.createElement('a');
      link.href = blobUrl;
      // Get extension from original image or default to jpg
      const extension = ad.sa_image.split('.').pop() || 'jpg';
      link.download = `SpringWay_Ad_${ad.sa_id}.${extension}`;

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Cleanup
      window.URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.error("Download failed:", error);
      Swal.fire({
        icon: 'error',
        title: 'Download Failed',
        text: 'The image file could not be found or downloaded. Please contact support.',
        background: '#1A1E2E',
        color: '#fff',
        confirmButtonColor: '#6366F1'
      });
    }
  };

  useEffect(() => {
    if (loading || shops.length === 0) return;

    // Mount fonts
    const link = document.createElement('link');
    link.href = 'https://fonts.googleapis.com/css2?family=Bebas+Neue&family=Outfit:wght@300;400;500;600;700;800&family=Space+Mono:wght@400;700&display=swap';
    link.rel = 'stylesheet';
    document.head.appendChild(link);

    // ─── Shop data ───────────────────────────────────────────────────────────────
    const SHOPS = shops;

    // ─── Three.js Setup ──────────────────────────────────────────────────────────
    const canvas = document.getElementById('three-canvas');
    const stickyWrapper = document.querySelector('.sticky-wrapper');
    if (!canvas || !stickyWrapper) return; // Wait until rendered
    const W = stickyWrapper.clientWidth, H = stickyWrapper.clientHeight;

    const renderer = new THREE.WebGLRenderer({ canvas, antialias: true, alpha: false });
    renderer.setSize(W, H);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.shadowMap.enabled = true;
    renderer.shadowMap.type = THREE.PCFSoftShadowMap;
    renderer.toneMapping = THREE.ACESFilmicToneMapping;
    renderer.toneMappingExposure = 0.9;

    renderer.outputColorSpace = THREE.SRGBColorSpace;

    const scene = new THREE.Scene();
    const atriumBase = new THREE.Color(0xFFF8E7); // Warm Luxury Champagne
    scene.background = atriumBase;
    scene.fog = new THREE.Fog(0xFFF8E7, 10, 100);

    // Dynamic FOV based on device
    scene.fog = new THREE.Fog(0x080C14, 1, 90);

    const isMobile = W < H;
    const camera = new THREE.PerspectiveCamera(isMobile ? 75 : 65, W / H, 0.1, 200);
    const initialY = isMobile ? 3.2 : 2.8;
    camera.position.set(0, initialY, 14);
    camera.lookAt(0, 2, 0);

    function makeTexCanvas(w, h, fn) {
      const c = document.createElement('canvas');
      c.width = w; c.height = h;
      fn(c.getContext('2d'), w, h);
      return new THREE.CanvasTexture(c);
    }

    // ─── Grand Mall Flooring (Highly Reflective) ────────────────────────────────
    const floorTex = makeTexCanvas(512, 512, (ctx, w, h) => {
      ctx.fillStyle = '#FFFFFF';
      ctx.fillRect(0, 0, w, h);
      ctx.strokeStyle = 'rgba(0, 0, 0, 0.03)';
      ctx.lineWidth = 1;
      for (let i = 0; i <= w; i += 64) { ctx.beginPath(); ctx.moveTo(i, 0); ctx.lineTo(i, h); ctx.stroke(); }
      for (let i = 0; i <= h; i += 64) { ctx.beginPath(); ctx.moveTo(0, i); ctx.lineTo(w, i); ctx.stroke(); }
      // High-End Polished Marble Border
      ctx.fillStyle = 'rgba(212, 175, 55, 0.05)';
      ctx.fillRect(w / 2 - 80, 0, 160, h);
    });
    floorTex.wrapS = floorTex.repeat.set(1, 10);
    floorTex.wrapT = THREE.RepeatWrapping;

    const floor = new THREE.Mesh(
      new THREE.PlaneGeometry(18, 120),
      new THREE.MeshStandardMaterial({ map: floorTex, roughness: 0.02, metalness: 0.45 })
    );
    floor.rotation.x = -Math.PI / 2;
    floor.position.set(0, 0, -42);
    floor.receiveShadow = true;
    scene.add(floor);

    // ─── Multi-Level Mezzanines & Pillars ────────────────────────────────────────
    const mezzanineMat = new THREE.MeshStandardMaterial({ color: 0xFFFFFF, roughness: 0.9 });
    const railingMat = new THREE.MeshStandardMaterial({ color: 0xFFFFFF, transparent: true, opacity: 0.3, roughness: 0.1, metalness: 0.8 });
    const pillarMat = new THREE.MeshStandardMaterial({ color: 0xFFFFFF, roughness: 0.1, metalness: 0.2 });

    for (let level = 1; level <= 2; level++) {
      const y = level * 8;
      [-1, 1].forEach(side => {
        const balcony = new THREE.Mesh(new THREE.PlaneGeometry(10, 120), mezzanineMat);
        balcony.position.set(side * 14, y, -42);
        balcony.rotation.x = -Math.PI / 2;
        scene.add(balcony);

        const rail = new THREE.Mesh(new THREE.BoxGeometry(0.1, 1.1, 120), railingMat);
        rail.position.set(side * 9.5, y + 0.55, -42);
        scene.add(rail);

        // Handrail
        const hand = new THREE.Mesh(new THREE.BoxGeometry(0.2, 0.05, 120), pillarMat);
        hand.position.set(side * 9.5, y + 1.1, -42);
        scene.add(hand);
      });
    }

    // Structural Pillars every 28 units
    for (let pz = 10; pz > -110; pz -= 28) {
      [-1, 1].forEach(side => {
        const pillar = new THREE.Mesh(new THREE.CylinderGeometry(0.5, 0.6, 30, 8), pillarMat);
        pillar.position.set(side * 9.5, 15, pz);
        scene.add(pillar);

        // Planter base
        const planter = new THREE.Mesh(new THREE.CylinderGeometry(1.2, 1, 0.8, 8), new THREE.MeshStandardMaterial({ color: 0x333333 }));
        planter.position.set(side * 9.5, 0.4, pz);
        scene.add(planter);

        // Simple 3D Greenery (Spheres)
        for (let gi = 0; gi < 3; gi++) {
          const leaf = new THREE.Mesh(new THREE.SphereGeometry(0.5), new THREE.MeshStandardMaterial({ color: 0x1E5128 }));
          leaf.position.set(side * 9.5 + (Math.random() - 0.5) * 0.8, 1, pz + (Math.random() - 0.5) * 0.8);
          scene.add(leaf);
        }
      });
    }

    // --- Grand Skylight Ceiling with Structural Truss ---
    const ceilingTex = makeTexCanvas(512, 512, (ctx, w, h) => {
      ctx.fillStyle = '#FFFFFF';
      ctx.fillRect(0, 0, w, h);
      ctx.fillStyle = 'rgba(123, 97, 241, 0.1)';
      ctx.fillRect(80, 80, 352, 352);
    });
    ceilingTex.wrapS = ceilingTex.wrapT = THREE.RepeatWrapping;
    ceilingTex.repeat.set(1, 6);

    const ceiling = new THREE.Mesh(new THREE.PlaneGeometry(40, 120), new THREE.MeshStandardMaterial({ map: ceilingTex }));
    ceiling.position.set(0, 24.1, -42); ceiling.rotation.x = Math.PI / 2; scene.add(ceiling);

    const skLightGeo = new THREE.PlaneGeometry(14, 120);
    const skLightMat = new THREE.MeshStandardMaterial({ color: 0xFFFDF0, emissive: 0xFFFDF0, emissiveIntensity: 1.5 });
    const skylight = new THREE.Mesh(skLightGeo, skLightMat);
    skylight.position.set(0, 24, -42); skylight.rotation.x = Math.PI / 2;
    scene.add(skylight);

    // --- Structural Truss Grid ---
    const trussMat = new THREE.MeshStandardMaterial({ color: 0x333333, metalness: 0.5, roughness: 0.2 });
    for (let tz = -10; tz > -110; tz -= 10) {
      const bar = new THREE.Mesh(new THREE.BoxGeometry(14, 0.4, 0.4), trussMat);
      bar.position.set(0, 23.95, tz);
      scene.add(bar);
    }
    [[-7, 23.95], [7, 23.95]].forEach(([tx, ty]) => {
      const longBar = new THREE.Mesh(new THREE.BoxGeometry(0.3, 0.3, 120), trussMat);
      longBar.position.set(tx, ty, -42);
      scene.add(longBar);
    });

    // --- Atmospheric Sparkle Particles ---
    const partGeo = new THREE.BufferGeometry();
    const partCount = 400;
    const posArr = new Float32Array(partCount * 3);
    for (let pi = 0; pi < partCount * 3; pi += 3) {
      posArr[pi] = (Math.random() - 0.5) * 30;
      posArr[pi + 1] = Math.random() * 24;
      posArr[pi + 2] = (Math.random() - 0.5) * 120 - 42;
    }
    partGeo.setAttribute('position', new THREE.BufferAttribute(posArr, 3));
    const partMat = new THREE.PointsMaterial({ color: 0xFFFFFF, size: 0.08, transparent: true, opacity: 0.4, blending: THREE.AdditiveBlending });
    const particles = new THREE.Points(partGeo, partMat);
    scene.add(particles);

    // --- Billboard Pedestrians ('Ghost Mall' fix) ---
    const ghostPeople = [];
    const makeGhostPerson = (x, y, z) => {
      const gTex = makeTexCanvas(128, 256, (ctx, w, h) => {
        ctx.fillStyle = 'rgba(255,255,255,0.15)';
        ctx.beginPath(); ctx.arc(w / 2, 40, 30, 0, Math.PI * 2); ctx.fill(); // Head
        ctx.fillRect(w / 2 - 40, 80, 80, 140); // Body
      });
      const gMat = new THREE.MeshStandardMaterial({ map: gTex, transparent: true, opacity: 0.4, side: THREE.DoubleSide });
      const ghost = new THREE.Mesh(new THREE.PlaneGeometry(1.2, 2.4), gMat);
      ghost.position.set(x, y + 1.2, z);
      scene.add(ghost);
      ghostPeople.push(ghost);
    };
    for (let gi = 0; gi < 20; gi++) {
      const gz = -gi * 6 - 10;
      makeGhostPerson((Math.random() - 0.5) * 8, 0, gz);
      if (gi % 4 === 0) makeGhostPerson(12, 8, gz); // Mezzanine ghost
      if (gi % 4 === 2) makeGhostPerson(-12, 8, gz);
    }


    const SPACING = 14;
    const shopObjects = [];

    function makeShopFacadeTex(shop) {
      return makeTexCanvas(512, 512, (ctx, w, h) => {
        // Luxury Interior Silhouette
        ctx.fillStyle = '#1A1E2E'; // Dark Interior Depth
        ctx.fillRect(0, 0, w, h);
        const wc = new THREE.Color(shop.accent);
        const accentCol = `rgba(${Math.round(wc.r * 255)}, ${Math.round(wc.g * 255)}, ${Math.round(wc.b * 255)}, 0.3)`;

        // Simulated Racks/Shelves in background
        ctx.fillStyle = 'rgba(255,255,255,0.05)';
        for (let ri = 0; ri < 6; ri++) {
          ctx.fillRect(40, 60 + ri * 70, 432, 2); // Shelf lines
          for (let si = 0; si < 4; si++) {
            ctx.fillRect(60 + si * 100, 10 + ri * 70, 40, 50); // Silhouette items
          }
        }

        // Interior Feature Wall (Glowing)
        ctx.fillStyle = accentCol;
        ctx.fillRect(w / 2 - 100, 0, 200, h);
        ctx.strokeStyle = `rgba(${Math.round(wc.r * 255)}, ${Math.round(wc.g * 255)}, ${Math.round(wc.b * 255)}, 0.6)`;
        ctx.lineWidth = 4;
        ctx.strokeRect(w / 2 - 100, 0, 200, h);

        // Mannequin/Feature silhouette
        ctx.fillStyle = 'rgba(0,0,0,0.4)';
        ctx.beginPath(); ctx.arc(w / 2, 200, 30, 0, Math.PI * 2); ctx.fill(); // Head
        ctx.fillRect(w / 2 - 40, 240, 80, 180); // Torso
      });
    }

    function makeSignTex(shop) {
      return makeTexCanvas(512, 128, (ctx, w, h) => {
        ctx.fillStyle = '#FFFFFF';
        ctx.fillRect(0, 0, w, h);
        const wc = new THREE.Color(shop.sign);
        ctx.fillStyle = `rgb(${Math.round(wc.r * 255)},${Math.round(wc.g * 255)},${Math.round(wc.b * 255)})`;
        ctx.textAlign = 'center';
        ctx.font = '900 48px "Space Mono", monospace, sans-serif';
        ctx.fillText((shop.virtual_mall_shop_name || 'SHOP').toUpperCase(), w / 2, h / 2 - 10);
        ctx.font = '600 22px "DM Sans", sans-serif';
        ctx.fillStyle = `rgba(${Math.round(wc.r * 255)},${Math.round(wc.g * 255)},${Math.round(wc.b * 255)}, 0.85)`;
        ctx.fillText((shop.tagline || '').toUpperCase(), w / 2, h / 2 + 30);
        ctx.strokeStyle = `rgba(${Math.round(wc.r * 255)},${Math.round(wc.g * 255)},${Math.round(wc.b * 255)},0.9)`;
        ctx.lineWidth = 6;
        ctx.strokeRect(4, 4, w - 8, h - 8);
      });
    }

    SHOPS.forEach((shop, i) => {
      const side = i % 2 === 0 ? 1 : -1;
      const z = -i * SPACING - 4;
      const xBase = side * 10;
      const group = new THREE.Group();
      scene.add(group);

      const bW = 8, bH = 8, bD = 5; // Standardized Height for luxury look
      const facadeTex = makeShopFacadeTex(shop);
      const sideTex = makeTexCanvas(256, 256, (ctx, w, h) => {
        ctx.fillStyle = '#F8F9FA'; // Architectural Stone/Panel
        ctx.fillRect(0, 0, w, h);
        ctx.strokeStyle = 'rgba(0,0,0,0.03)';
        for (let y = 0; y < h; y += 32) { ctx.beginPath(); ctx.moveTo(0, y); ctx.lineTo(w, y); ctx.stroke(); }
        for (let x = 0; x < w; x += 64) { ctx.beginPath(); ctx.moveTo(x, 0); ctx.lineTo(x, h); ctx.stroke(); }
      });

      const mats = [
        new THREE.MeshStandardMaterial({ map: sideTex, roughness: 0.85 }),
        new THREE.MeshStandardMaterial({ map: sideTex, roughness: 0.85 }),
        new THREE.MeshStandardMaterial({ color: 0xF8F9FA, roughness: 0.9 }),
        new THREE.MeshStandardMaterial({ color: 0xF8F9FA, roughness: 0.9 }),
        new THREE.MeshStandardMaterial({ map: facadeTex, roughness: 0.9, metalness: 0 }), // Rear interior
        new THREE.MeshStandardMaterial({ map: sideTex, roughness: 0.85 }),
      ];

      const building = new THREE.Mesh(new THREE.BoxGeometry(bW, bH, bD), mats);
      building.position.set(xBase, bH / 2, z); building.castShadow = true; building.receiveShadow = true;
      group.add(building);

      // --- 3D Storefront Frame (Brushed Metal) ---
      const frameMat = new THREE.MeshStandardMaterial({ color: 0x222222, metalness: 0.9, roughness: 0.2 });
      const frame = new THREE.Mesh(new THREE.BoxGeometry(bW + 0.2, bH, 0.4), frameMat);
      frame.position.set(xBase, bH / 2, z + (side > 0 ? -bD / 2 - 0.2 : bD / 2 + 0.2));
      group.add(frame);

      // --- Store Glass Front (Reflective) ---
      const glassMat = new THREE.MeshStandardMaterial({ color: 0x88CCFF, transparent: true, opacity: 0.15, roughness: 0.05, metalness: 0.3 });
      const glass = new THREE.Mesh(new THREE.PlaneGeometry(bW - 0.4, bH - 0.4), glassMat);
      glass.position.set(xBase, bH / 2, z + (side > 0 ? -bD / 2 - 0.41 : bD / 2 + 0.41));
      glass.rotation.y = side > 0 ? 0 : Math.PI;
      group.add(glass);

      // --- Shop Interior Glow ---
      const warmLight = new THREE.PointLight(0xFFAA66, 1.5, 8); // Warm Interior Glow
      warmLight.position.set(xBase, 4, z);
      scene.add(warmLight);
      const neonStripGeo = new THREE.CylinderGeometry(0.04, 0.04, bH, 6);
      const neonStripMat = new THREE.MeshStandardMaterial({ color: shop.accent, emissive: shop.accent, emissiveIntensity: 2.0 });
      [[-bW / 2, -bD / 2], [bW / 2, -bD / 2], [-bW / 2, bD / 2], [bW / 2, bD / 2]].forEach(([nx, nz]) => {
        const strip = new THREE.Mesh(neonStripGeo, neonStripMat);
        strip.position.set(xBase + nx, bH / 2, z + nz);
        group.add(strip);
      });
      if (shop.image) {
        const loader = new THREE.TextureLoader();
        loader.setCrossOrigin('anonymous');
        loader.load(shop.image, (imgTex) => {
          imgTex.colorSpace = THREE.SRGBColorSpace;
          const posV = (bH * 0.62) - 1.95;
          const frameMesh = new THREE.Mesh(new THREE.BoxGeometry(4.2, 3.1, 0.1), new THREE.MeshStandardMaterial({ color: 0x1A1E2E, roughness: 0.5, metalness: 0.3 }));
          const faceZ = z + (side > 0 ? -bD / 2 - 0.06 : bD / 2 + 0.06);
          frameMesh.position.set(xBase, posV, faceZ);
          frameMesh.rotation.y = side > 0 ? 0 : Math.PI;
          group.add(frameMesh);
          const imgMesh = new THREE.Mesh(new THREE.PlaneGeometry(4.0, 2.9), new THREE.MeshStandardMaterial({ map: imgTex, roughness: 0.6, side: THREE.DoubleSide }));
          const imgZ = faceZ + (side > 0 ? -0.06 : 0.06);
          imgMesh.position.set(xBase, posV, imgZ);
          imgMesh.rotation.y = side > 0 ? 0 : Math.PI;
          group.add(imgMesh);
        });
      }
      const roofEdge = new THREE.Mesh(new THREE.BoxGeometry(bW + 0.2, 0.15, bD + 0.2), new THREE.MeshStandardMaterial({ color: shop.accent, emissive: shop.accent, emissiveIntensity: 0.3, roughness: 0.6 }));
      roofEdge.position.set(xBase, bH + 0.07, z);
      group.add(roofEdge);
      // --- 3D Illuminated Shop Name Box ---
      const signTex = makeSignTex(shop);
      const signMat = new THREE.MeshStandardMaterial({ map: signTex, transparent: true, emissive: new THREE.Color(shop.sign), emissiveIntensity: 1.2, emissiveMap: signTex });
      const signBox = new THREE.Mesh(new THREE.BoxGeometry(5.4, 1.5, 0.6), new THREE.MeshStandardMaterial({ color: 0x111111, roughness: 0.2 }));
      signBox.position.set(xBase, bH * 0.75, z + (side > 0 ? -bD / 2 - 0.5 : bD / 2 + 0.5));
      group.add(signBox);

      const signF = new THREE.Mesh(new THREE.PlaneGeometry(5.2, 1.3), signMat);
      signF.position.set(xBase, bH * 0.75, z + (side > 0 ? -bD / 2 - 0.81 : bD / 2 + 0.81));
      signF.rotation.y = side > 0 ? 0 : Math.PI;
      group.add(signF);
      const signL = new THREE.Mesh(new THREE.PlaneGeometry(4.2, 1.1), signMat);
      signL.position.set(xBase - bW / 2 - 0.02, bH * 0.62, z);
      signL.rotation.y = -Math.PI / 2;
      group.add(signL);
      const signR = new THREE.Mesh(new THREE.PlaneGeometry(4.2, 1.1), signMat);
      signR.position.set(xBase + bW / 2 + 0.02, bH * 0.62, z);
      signR.rotation.y = Math.PI / 2;
      group.add(signR);
      const shopLight = new THREE.PointLight(shop.accent, 4.0, 16);
      shopLight.position.set(xBase, 2.5, z + (side > 0 ? -bD / 2 - 1 : bD / 2 + 1));
      shopLight.castShadow = true;
      shopLight.shadow.mapSize.set(512, 512);
      scene.add(shopLight);
      shopObjects.push({ group, light: shopLight, z, i, shop, building, bH });
    });

    const ambientLight = new THREE.AmbientLight(0xffffff, 1.4);
    scene.add(ambientLight);
    [[-60, 20], [-30, 20], [0, 20], [30, 20], [60, 20]].forEach(([z, x]) => {
      const pLight = new THREE.PointLight(0xFFF8E7, 3.5, 40); // Champagne point lights
      pLight.position.set(x, 15, -z);
      scene.add(pLight);
    });
    const characterLight = new THREE.PointLight(0x6366F1, 1.8, 14);
    scene.add(characterLight);


    const TOTAL_SHOPS = SHOPS.length;
    const STREET_LEN = (TOTAL_SHOPS > 1 ? TOTAL_SHOPS - 1 : 1) * SPACING;

    let scrollProgress = 0;
    let targetProgress = 0;

    const containerEl = containerRef.current;
    const handleScroll = () => {
      if (!containerEl) return;
      const rect = containerEl.getBoundingClientRect();
      const maxScroll = containerEl.offsetHeight - window.innerHeight;
      const scrolled = Math.max(0, -rect.top);
      targetProgress = maxScroll > 0 ? Math.max(0, Math.min(1, scrolled / maxScroll)) : 0;
      const progressEl = document.getElementById('progress');
      const hudFillEl = document.getElementById('hud-fill');
      if (progressEl) progressEl.style.width = (targetProgress * 100) + '%';
      if (hudFillEl) hudFillEl.style.width = (targetProgress * 100) + '%';
    };
    window.addEventListener('scroll', handleScroll, { passive: true });

    let lastShopIdx = -1;
    let lastVisibleState = false;

    function updateShopCard(progress) {
      const pos = progress * (TOTAL_SHOPS > 1 ? TOTAL_SHOPS - 1 : 1);
      const idx = Math.max(0, Math.min(Math.round(pos), TOTAL_SHOPS - 1));
      if (idx !== lastShopIdx && idx >= 0) {
        lastShopIdx = idx;
        setCurrentShop(shops[idx]);
        setCurrentIdx(idx);
        setActiveSide(idx % 2 === 0 ? 'right' : 'left');
      }
      const shouldBeVisible = progress >= 0.02;
      if (shouldBeVisible !== lastVisibleState) {
        lastVisibleState = shouldBeVisible;
        setCardVisible(shouldBeVisible);
      }
    }

    const mm = document.getElementById('minimap-canvas');
    let mmCtx = null;
    if (mm) {
      mm.width = 120; mm.height = 60;
      mmCtx = mm.getContext('2d');
    }

    function drawMinimap(progress) {
      if (!mmCtx) return;
      mmCtx.clearRect(0, 0, 120, 60);
      mmCtx.fillStyle = 'rgba(10,14,28,0.6)';
      mmCtx.fillRect(0, 0, 120, 60);
      mmCtx.fillStyle = 'rgba(0,245,196,0.1)';
      mmCtx.fillRect(50, 4, 20, 52);
      SHOPS.forEach((s, i) => {
        const side = i % 2 === 0 ? 1 : -1;
        const y = 4 + (TOTAL_SHOPS > 1 ? i / (TOTAL_SHOPS - 1) : 0) * 52;
        mmCtx.fillStyle = '#' + new THREE.Color(s.accent).getHexString();
        mmCtx.fillRect(side > 0 ? 72 : 30, y - 3, 16, 6);
      });
      const cy = 4 + progress * 52;
      mmCtx.fillStyle = '#00F5C4';
      mmCtx.beginPath(); mmCtx.arc(60, cy, 3, 0, Math.PI * 2); mmCtx.fill();
    }

    const clock = new THREE.Clock();
    let animId;

    function animate() {
      animId = requestAnimationFrame(animate);
      const t = clock.getElapsedTime();
      scrollProgress += (targetProgress - scrollProgress) * 0.06;

      // Update Particles
      if (particles) particles.rotation.y += 0.001;

      // Billboard Ghosts (Always face camera)
      ghostPeople.forEach(g => { g.lookAt(camera.position.x, g.position.y, camera.position.z); });

      const zPos = 14 - scrollProgress * STREET_LEN;
      const sway = Math.sin(t * 0.3) * 0.06;
      const bob = Math.sin(t * 0.5) * 0.04;
      const isMobile = renderer.domElement.width < renderer.domElement.height;
      const camY = (isMobile ? 3.2 : 2.8) + bob;
      camera.position.x += (sway - camera.position.x) * 0.02;
      camera.position.y = camY;
      camera.position.z += (zPos - camera.position.z) * 0.08;
      camera.lookAt(camera.position.x * 0.3, 2.5, camera.position.z - 6);
      characterLight.position.copy(camera.position);
      shopObjects.forEach((obj, i) => {
        const dist = Math.abs(camera.position.z - obj.z);
        const isNear = dist < 24;
        obj.light.intensity = isNear ? 2.5 + Math.sin(t * 1.5 + i) * 0.3 : 0.5;
        const targetScale = isNear ? 1.0 : 0.98;
        obj.building.scale.y += (targetScale - obj.building.scale.y) * 0.05;
      });
      updateShopCard(scrollProgress);
      drawMinimap(scrollProgress);
      const hud = document.getElementById('hud');
      if (hud && hud.classList) hud.classList.toggle('hidden', scrollProgress > 0.05);
      renderer.render(scene, camera);
    }

    const onResize = () => {
      if (!stickyWrapper) return;
      const w = stickyWrapper.clientWidth, h = stickyWrapper.clientHeight;
      const isMobile = w < h;
      camera.fov = isMobile ? 75 : 65;
      camera.aspect = w / h;
      camera.updateProjectionMatrix();
      renderer.setSize(w, h);
    };
    window.addEventListener('resize', onResize);

    document.getElementById('minimap')?.classList.add('show');
    document.getElementById('street-sign')?.classList.add('show');
    animate();

    return () => {
      window.removeEventListener('resize', onResize);
      window.removeEventListener('scroll', handleScroll);
      if (animId) cancelAnimationFrame(animId);
      if (renderer) renderer.dispose();
      document.head.removeChild(link);
    };
  }, [loading, shops]);

  return (
    <div className="virtual-mall-container" ref={containerRef}>
      <div className="sticky-wrapper">
        {/* 3D Canvas */}
        <div id="canvas-wrap"><canvas id="three-canvas"></canvas></div>

        {/* Top Nav */}
        <div className="nova-nav">
          <div className="nav-logo">SPRINGWAY</div>
          <div className="mall-search">
            <div className="mall-search__box">
              <TbSearch className="mall-search__icon" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onFocus={() => setIsSearchFocused(true)}
                onBlur={() => setTimeout(() => setIsSearchFocused(false), 200)}
                placeholder="Search shops, district, code..."
                className="mall-search__input"
              />
              {searchTerm && (
                <button
                  type="button"
                  className="mall-search__clear"
                  onClick={() => setSearchTerm('')}
                  aria-label="Clear shop search"
                >
                  <TbX />
                </button>
              )}
            </div>
            {filteredShops.length > 0 && (
              <div className="mall-search__results">
                {filteredShops.slice(0, 6).map((shop) => (
                  <button
                    key={shop.id}
                    type="button"
                    className="mall-search__result"
                    onClick={() => jumpToShop(shop, true)}
                  >
                    <span className="mall-search__result-name">{shop.virtual_mall_shop_name}</span>
                    <span className="mall-search__result-meta">
                      {[shop.category, shop.district, shop.code].filter(Boolean).join(' • ')}
                    </span>
                  </button>
                ))}
              </div>
            )}
          </div>
          <div className="nav-hint">
            <span className="pulse-dot"></span> LIVE ATRIUM
          </div>
        </div>

        {/* Wallet HUDs */}
        <div className="mall-wallet-hud">
          <div className="hud-stat-card">
            <div className="hud-stat-label">
              <div className="hud-stat-dot"></div>
              PURCHASE POINT
            </div>
            <div className="hud-stat-value">
              ₹{(purchasePoints || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </div>
            <div className="hud-stat-bar"><div className="hud-stat-fill"></div></div>
          </div>
        </div>

        <div className="mall-activity-hud">
          <div className="hud-stat-card activity">
            <div className="hud-stat-label">
              <div className="hud-stat-dot activity"></div>
              ACTIVITY WALLET
            </div>
            <div className="hud-stat-value">
              ₹{(activityWallet || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </div>
            <div className="hud-stat-bar"><div className="hud-stat-fill activity"></div></div>
          </div>


        </div>

        {/* UI */}
        <div id="progress"></div>


        {!isSearchFocused && !searchTerm && ads.map((ad, index) => (
          <AdCard
            key={ad.sa_id}
            ad={ad}
            index={index}
            activeAdIndex={activeAdIndex}
            isHoveredRef={isHoveredRef}
            adLikes={adLikes}
            adComments={adComments}
            handleNextAd={handleNextAd}
            handlePrevAd={handlePrevAd}
            handleLike={handleLike}
            handleComment={handleComment}
            handleDownload={handleDownload}
            imgBaseurl={imgBaseurl}
          />
        ))}


        {loading && (
          <div className="loading-overlay">
            <div className="loader"></div>
            <p>Initializing Virtual Mall...</p>
          </div>
        )}

        {!loading && shops.length === 0 && (
          <div className="loading-overlay">
            <p>No shops currently available in the mall.</p>
          </div>
        )}

        <div id="crosshair">
          <div className="ch-h"></div>
          <div className="ch-v"></div>
          <div className="ch-dot"></div>
        </div>

        <div id="hud">
          <div className="hud-pill">
            <div className="mouse-icon"><div className="mouse-wheel"></div></div>
            <div className="hud-scroll">SCROLL TO NAVIGATE</div>
          </div>
          <div className="hud-bar"><div className="hud-fill" id="hud-fill"></div></div>
        </div>

        {/* MERCHANT SHOP IDENTITY CARD */}
        <div
          className={`merchant-banner ${cardVisible && !isSearchFocused ? 'visible' : ''}`}
          style={{ '--merchant-accent': '#' + new THREE.Color(currentShop?.accent || 0x6366F1).getHexString() }}
          onClick={handleShopClick}
        >
          <div className="merchant-banner-bg-glow"></div>
          <div className="merchant-banner-inner">
            <div className="ornament ornament-tl">✦</div>
            <div className="ornament ornament-tr">✦</div>
            <div className="merchant-img-wrap">
              <img src={currentShop?.image} className="merchant-img" alt={currentShop?.virtual_mall_shop_name} onError={(e) => { e.target.src = 'https://via.placeholder.com/150/1A1E2E/FFFFFF/?text=STORE'; }} />
            </div>
            <div className="merchant-divider" style={{ backgroundColor: '#' + new THREE.Color(currentShop?.accent || 0xffffff).getHexString() }}></div>
            <div className="merchant-info">
              <div className="merchant-status-row">
                {/* <div className="merchant-cat" style={{ color: '#' + new THREE.Color(currentShop?.accent || 0xffffff).getHexString(), borderColor: '#' + new THREE.Color(currentShop?.accent || 0xffffff).getHexString() }}>{currentShop?.cat || 'Boutique'}</div> */}
                {/* <div className={`merchant-open-badge ${currentShop?.open ? 'open' : 'closed'}`}>
                  <div className="merchant-dot"></div> {currentShop?.open ? 'OPEN NOW' : 'CLOSED'}
                </div> */}
              </div>
              <h2 className="merchant-name" style={{ color: '#' + new THREE.Color(currentShop?.accent || 0xffffff).getHexString() }}>
                {currentShop?.virtual_mall_shop_name || 'Shop Name'}
              </h2>
              <div className="merchant-address">
                <TbMapPin className="merchant-address-icon" />
                {currentShop?.tagline || 'Shop Location Level 1'}
              </div>
            </div>
            {/* <div className="merchant-action">
              <button 
                className="visit-store-btn" 
                style={{ backgroundColor: '#' + new THREE.Color(currentShop?.accent || 0x6366F1).getHexString() }}
                onClick={() => { alert(`Welcome to ${currentShop?.virtual_mall_shop_name}! Store entry integration goes here.`); }}
              >
                ENTER STORE
                <span className="btn-arrow">→</span>
              </button>
            </div> */}
            <div className="merchant-divider divider-right" style={{ backgroundColor: '#' + new THREE.Color(currentShop?.accent || 0xffffff).getHexString() }}></div>
            <div className="merchant-counter-wrapper">
              <button 
                className="merchant-nav-btn" 
                onClick={(e) => { e.stopPropagation(); if (currentIdx > 0) jumpToShop(shops[currentIdx - 1]); }}
                disabled={currentIdx === 0}
                aria-label="Previous shop"
              >
                <TbChevronLeft />
              </button>
              <div className="merchant-counter">
                <span className="merchant-counter-num">{currentIdx + 1}</span>
                <span className="merchant-counter-sep">/</span>
                <span className="merchant-counter-total">{shops.length}</span>
              </div>
              <button 
                className="merchant-nav-btn" 
                onClick={(e) => { e.stopPropagation(); if (currentIdx < shops.length - 1) jumpToShop(shops[currentIdx + 1]); }}
                disabled={currentIdx === shops.length - 1}
                aria-label="Next shop"
              >
                <TbChevronRight />
              </button>
            </div>
          </div>
        </div>

        <div id="minimap"><canvas id="minimap-canvas"></canvas></div>

      </div>
    </div>
  );
};

export default VirtualMall;
